/**
 * Recipe Utility Functions
 * Shared utility functions for recipe-related operations
 */

import { toast } from 'react-toastify';
import { mbmr_overall_pdf_report } from '../api';

/**
 * Generate avatar letters from name
 * @param {string} name - Product/Recipe name
 * @returns {string} - Two letter acronym
 */
export const getAvatarLetters = (name) => {
  if (!name) return '';
  const words = name.trim().split(' ');
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Format ingredient data into readable strings
 * @param {Array} data - Ingredient data array
 * @param {string} header - Header type ('operation' or other)
 * @returns {Array} - Formatted ingredient strings
 */
export const formatIngredientStrings = (data, header) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((ingredient, index) => {
    let ingredientString = `${ingredient.material_name || ''} `;

    if (header === 'operation') {
      ingredientString += '';
    } else {
      ingredientString += `: ${ingredient.quantity || 0}`;
      if (ingredient.parameter_name !== '') {
        ingredientString += `-${ingredient.parameter_name}: Min ${ingredient.min_value} ${ingredient.parameter_name_unit} and Max ${ingredient.max_value} ${ingredient.parameter_name_unit}`;
      } else {
        ingredientString += `-`;
      }
    }

    ingredientString += index === data.length - 1 ? '' : ',';
    return ingredientString;
  });
};

/**
 * Add unique keys to data for Ant Design Collapse component
 * @param {Array} filterData - Data array to add keys
 * @returns {Array} - Data with keys added
 */
export const addKeysForCollapse = (filterData) => {
  if (!filterData || !Array.isArray(filterData)) return [];

  return filterData.map((item, index) => ({
    ...item,
    key: index.toString(),
  }));
};

/**
 * Download recipe PDF with proper error handling
 * @param {string} recipeId - Recipe ID to download
 * @returns {Promise<void>}
 */
export const downloadRecipePdf = async (recipeId) => {
  if (!recipeId) {
    toast.error('Recipe ID is required');
    return;
  }

  try {
    toast.info('Preparing download...');

    await mbmr_overall_pdf_report({ recipe_id: recipeId }, (res) => {
      if (res?.data?.status === 'success' && res?.data?.data) {
        try {
          // Decode base64 PDF data
          const base64Data = res.data.data;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });

          // Create download link
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `recipe_${recipeId}_${Date.now()}.pdf`;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);

          toast.success('Download complete');
        } catch (err) {
          console.error('Error decoding PDF:', err);
          toast.error('Failed to process PDF data');
        }
      } else {
        toast.warning('No PDF data available');
      }
    });
  } catch (error) {
    console.error('PDF download error:', error);
    toast.error('Failed to download PDF');
  }
};

/**
 * Decode base64 string to blob
 * @param {string} base64Data - Base64 encoded data
 * @param {string} contentType - MIME type
 * @returns {Blob} - Decoded blob
 */
export const base64ToBlob = (base64Data, contentType = 'application/pdf') => {
  try {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  } catch (error) {
    console.error('Base64 decode error:', error);
    throw new Error('Failed to decode base64 data');
  }
};

/**
 * Filter array by search value
 * @param {Array} data - Data array to filter
 * @param {string} searchValue - Search term
 * @param {string} key - Object key to search in
 * @returns {Array} - Filtered data
 */
export const filterBySearch = (data, searchValue, key) => {
  if (!searchValue || !data || !Array.isArray(data)) return data;

  const searchLower = searchValue.toLowerCase().trim();
  return data.filter((item) =>
    item[key]?.toLowerCase()?.includes(searchLower)
  );
};

/**
 * Get workflow stage information based on status
 * @param {Object} record - Recipe record
 * @param {string} status - Current status
 * @returns {Object} - Stage information
 */
export const getWorkflowStage = (record, status) => {
  const stageMapping = {
    Draft: {
      step: 0,
      title: 'Draft',
      color: 'default',
      description: 'Recipe in draft creation',
    },
    'Internal Review': {
      step: 1,
      title: 'Internal Review',
      color: 'processing',
      description: 'Under internal review',
    },
    Pending: {
      step: 2,
      title: 'Pending Approval',
      color: 'warning',
      description: 'Awaiting QA/Manager approval',
    },
    Approved: {
      step: 3,
      title: 'Approved',
      color: 'success',
      description: 'QA/Manager approved',
    },
    Finalized: {
      step: 4,
      title: 'Released',
      color: 'success',
      description: 'Active in production',
    },
    Finalize: {
      step: 4,
      title: 'Released',
      color: 'success',
      description: 'Active in production',
    },
    Rejected: {
      step: 2,
      title: 'Rejected',
      color: 'error',
      description: 'Requires revision',
    },
    Archived: {
      step: 5,
      title: 'Archived',
      color: 'default',
      description: 'Archived / Superseded',
    },
  };

  const recipeStatus = status || record?.approval_status || 'Draft';

  return stageMapping[recipeStatus] || {
    step: 0,
    title: recipeStatus,
    color: 'default',
    description: 'Current stage',
  };
};

/**
 * Build URL with query parameters
 * @param {string} baseUrl - Base URL
 * @param {Object} params - Query parameters
 * @returns {string} - URL with params
 */
export const buildUrlWithParams = (baseUrl, params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Parse query parameters from URL
 * @param {string} search - URL search string
 * @returns {Object} - Parsed params object
 */
export const parseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  const result = {};

  for (const [key, value] of params.entries()) {
    result[key] = value;
  }

  return result;
};

/**
 * Validate recipe data before submission
 * @param {Object} recipeData - Recipe data to validate
 * @returns {Object} - { valid: boolean, errors: Array }
 */
export const validateRecipeData = (recipeData) => {
  const errors = [];

  if (!recipeData.recipe_name?.trim()) {
    errors.push('Recipe name is required');
  }

  if (!recipeData.product_id) {
    errors.push('Product selection is required');
  }

  // Add more validation rules as needed

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Format recipe status for display
 * @param {string} status - Raw status value
 * @returns {string} - Formatted status
 */
export const formatRecipeStatus = (status) => {
  if (!status) return 'Unknown';

  const statusMap = {
    Finalize: 'Finalized',
    'Approval Pending': 'Pending',
  };

  return statusMap[status] || status;
};
